import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { examService, questionService, aiService } from '../services/api';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import './ExamPage.css';

function ExamPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState({}); // Controla feedback por questão
  const [jumpTo, setJumpTo] = useState('');
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [aiAnswer, setAiAnswer] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiQuestionId, setAiQuestionId] = useState(null);
  const [askPrompt, setAskPrompt] = useState('');
  const [askResult, setAskResult] = useState(null);
  const [askLoading, setAskLoading] = useState(false);
  const [askError, setAskError] = useState('');

  useEffect(() => {
    loadExam();
  }, [examId]);

  const loadExam = async () => {
    try {
      const examData = await examService.getExamById(examId);
      setExam(examData);
      setRemainingSeconds((examData.durationMinutes || 180) * 60);

      // Start exam
      await examService.startExam(examId);

      // Load questions based on source filter
      let allQuestions;
      if (examData.sourceFilter && examData.sourceFilter !== 'all') {
        const isSourceType = ['dump', 'learning_journey', 'misc'].includes(examData.sourceFilter);
        if (isSourceType) {
          allQuestions = await questionService.getQuestionsBySourceType(examData.sourceFilter);
        } else {
          allQuestions = await questionService.getQuestionsBySourceName(examData.sourceFilter);
        }
      } else {
        allQuestions = await questionService.getAllQuestions();
      }

      const shouldShuffle = ['all', 'dump', 'learning_journey'].includes(examData.sourceFilter);
      const ordered = [...allQuestions].sort((a, b) => {
        const aKey = a.externalId ?? a.id ?? 0;
        const bKey = b.externalId ?? b.id ?? 0;
        return aKey - bKey;
      });
      const finalQuestions = shouldShuffle
        ? ordered.sort(() => 0.5 - Math.random())
        : ordered;
      setQuestions(finalQuestions.slice(0, examData.totalQuestions));

      setLoading(false);
    } catch (error) {
      console.error('Error loading exam:', error);
      alert('Erro ao carregar simulado');
      navigate('/');
    }
  };

  useEffect(() => {
    if (!exam || loading) {
      return;
    }

    const intervalId = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) {
          clearInterval(intervalId);
          handleFinish();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [exam, loading]);

  const handleAnswer = (optionLetter) => {
    const currentQuestion = questions[currentIndex];

    // Não permitir mudar resposta se já confirmou (modo imediato)
    if (exam.correctionMode === 'immediate' && showFeedback[currentQuestion.id]) {
      return;
    }

    const newAnswers = {...answers};

    if (currentQuestion.questionType === 'multiple_choice') {
      if (!newAnswers[currentQuestion.id]) {
        newAnswers[currentQuestion.id] = [];
      }
      if (newAnswers[currentQuestion.id].includes(optionLetter)) {
        newAnswers[currentQuestion.id] = newAnswers[currentQuestion.id].filter(a => a !== optionLetter);
      } else {
        newAnswers[currentQuestion.id].push(optionLetter);
      }
    } else {
      newAnswers[currentQuestion.id] = [optionLetter];

      // No modo imediato com single choice, confirma automaticamente
      if (exam.correctionMode === 'immediate') {
        setShowFeedback({...showFeedback, [currentQuestion.id]: true});
      }
    }

    setAnswers(newAnswers);
  };

  const confirmAnswer = () => {
    const currentQuestion = questions[currentIndex];
    setShowFeedback({...showFeedback, [currentQuestion.id]: true});
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach(q => {
      const userAnswer = answers[q.id] || [];
      const correctAnswers = q.options.filter(opt => opt.isCorrect).map(opt => opt.optionLetter).sort();
      const userAnswerSorted = [...userAnswer].sort();

      if (JSON.stringify(correctAnswers) === JSON.stringify(userAnswerSorted)) {
        correct++;
      }
    });

    return ((correct / questions.length) * 100).toFixed(2);
  };

  const handleFinish = async () => {
    const score = calculateScore();
    await examService.completeExam(examId, score);
    navigate(`/results/${examId}`, { state: { score, answers, questions } });
  };

  const handleFinishEarly = async () => {
    const confirmFinish = window.confirm('Deseja finalizar agora? Questões não respondidas serão consideradas incorretas.');
    if (!confirmFinish) {
      return;
    }
    await handleFinish();
  };

  const handleExit = () => {
    const confirmExit = window.confirm('Deseja sair do simulado? O progresso atual sera perdido.');
    if (confirmExit) {
      navigate('/');
    }
  };

  const handleJump = () => {
    const parsed = parseInt(jumpTo, 10);
    if (Number.isNaN(parsed)) {
      return;
    }
    const clamped = Math.min(Math.max(parsed, 1), questions.length);
    setCurrentIndex(clamped - 1);
  };

  const handleAiAnswer = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      const noteText = currentQuestion.questionNote || '';
      const match = noteText.match(/(\d+)\s+correct answers?/i);
      let maxAnswersFromNote = match ? parseInt(match[1], 10) : null;
      if (!maxAnswersFromNote && /\b(three|tres|três)\b/i.test(noteText)) {
        maxAnswersFromNote = 3;
      }
      const maxAnswers = currentQuestion.questionType === 'multiple_choice'
        ? (maxAnswersFromNote || 2)
        : 1;
      const payload = {
        question: currentQuestion.questionText,
        question_type: currentQuestion.questionType,
        options: currentQuestion.options.map(opt => ({
          letter: opt.optionLetter,
          text: opt.optionText,
        })),
        top_k: 3,
        max_answers: Math.min(maxAnswers, currentQuestion.options.length),
        llm_model: localStorage.getItem('aiModel') || 'qwen2.5:7b',
      };
      const response = await aiService.answerQuestion(payload);
      setAiAnswer(response);
    } catch (error) {
      console.error('Error fetching AI answer:', error);
      setAiError('Erro ao consultar IA.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiAsk = async () => {
    const question = askPrompt.trim();
    if (!question) {
      setAskError('Digite uma pergunta para a IA.');
      return;
    }
    setAskLoading(true);
    setAskError('');
    setAskResult(null);
    try {
      const payload = {
        question,
        top_k: 4,
        llm_model: localStorage.getItem('aiModel') || 'qwen2.5:7b',
      };
      const response = await aiService.askQuestion(payload);
      setAskResult(response);
    } catch (error) {
      console.error('Error asking AI:', error);
      setAskError('Erro ao consultar IA.');
    } finally {
      setAskLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const clamped = Math.max(0, seconds);
    const hours = Math.floor(clamped / 3600);
    const minutes = Math.floor((clamped % 3600) / 60);
    const secs = clamped % 60;
    const pad = (value) => String(value).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  };

  useEffect(() => {
    if (!questions.length) {
      return;
    }
    const question = questions[currentIndex];
    if (aiQuestionId !== question?.id) {
      setAiAnswer(null);
      setAiError('');
      setAiQuestionId(question?.id || null);
    }
  }, [currentIndex, questions, aiQuestionId]);

  if (loading) {
    return <div className="loading"><div><div className="spinner"></div><p>Carregando simulado...</p></div></div>;
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="exam-page-container">
      <div className="container">
        {/* Header */}
        <div className="exam-header card">
          <div className="exam-info">
            <h2>{exam.title}</h2>
            <p>Questão {currentIndex + 1} de {questions.length}</p>
          </div>
          <div className="exam-timer">
            ⏱️ {formatTime(remainingSeconds)}
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{width: `${progress}%`}}></div>
          </div>
        </div>

        {/* Question */}
        <div className="question-card card">
          <div className="question-header">
            <span className="question-number">Questão {currentIndex + 1}</span>
            <span className="question-category">{currentQuestion.category?.name || 'Geral'}</span>
          </div>

          <h3 className="question-text">{currentQuestion.questionText}</h3>

          {currentQuestion.questionNote && (
            <p className="question-note">{currentQuestion.questionNote}</p>
          )}

          <div className="options">
            {currentQuestion.options.map(option => {
              const isSelected = answers[currentQuestion.id]?.includes(option.optionLetter);
              const isCorrect = option.isCorrect;
              const feedbackMode = exam.correctionMode === 'immediate' && showFeedback[currentQuestion.id];

              let optionClass = 'option';
              if (isSelected) optionClass += ' selected';
              if (feedbackMode) {
                if (isCorrect) {
                  optionClass += ' correct';
                } else if (isSelected && !isCorrect) {
                  optionClass += ' wrong';
                }
              }

              return (
                <div
                  key={option.id}
                  className={optionClass}
                  onClick={() => handleAnswer(option.optionLetter)}
                >
                  <span className="option-letter">{option.optionLetter}</span>
                  <span className="option-text">{option.optionText}</span>
                  {feedbackMode && (
                    <span className="feedback-icon">
                      {isCorrect ? '✓' : (isSelected ? '✗' : '')}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Explicação no modo imediato */}
          {exam.correctionMode === 'immediate' && showFeedback[currentQuestion.id] && (
            <div className="immediate-feedback">
              {(() => {
                const userAnswer = answers[currentQuestion.id] || [];
                const correctAnswers = currentQuestion.options.filter(opt => opt.isCorrect).map(opt => opt.optionLetter);
                const isCorrect = JSON.stringify([...userAnswer].sort()) === JSON.stringify([...correctAnswers].sort());

                return (
                  <div className={`feedback-box ${isCorrect ? 'correct' : 'incorrect'}`}>
                    <div className="feedback-header">
                      {isCorrect ? '✅ Resposta Correta!' : '❌ Resposta Incorreta'}
                    </div>
                    {!isCorrect && (
                      <div className="feedback-correct-answer">
                        <strong>Resposta correta:</strong> {correctAnswers.join(', ')}
                      </div>
                    )}
                    {currentQuestion.explanation && currentQuestion.explanation.trim() && (
                      <div className="feedback-explanation">
                        <strong>💡 Explicação:</strong>
                        <p>{currentQuestion.explanation}</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="navigation card">
          <div className="jump-group">
            <input
              type="number"
              min="1"
              max={questions.length}
              value={jumpTo}
              onChange={(e) => setJumpTo(e.target.value)}
              className="jump-input"
              placeholder="Nº questão"
            />
            <button
              className="btn btn-secondary"
              onClick={handleJump}
            >
              Ir para questão
            </button>
          </div>
          <button
            className="btn btn-danger"
            onClick={handleExit}
          >
            Sair do Simulado
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
          >
            ← Anterior
          </button>

          {/* Botão Confirmar no modo imediato (multiple choice) */}
          {exam.correctionMode === 'immediate' &&
           currentQuestion.questionType === 'multiple_choice' &&
           !showFeedback[currentQuestion.id] && (
            <button
              className="btn btn-warning"
              onClick={confirmAnswer}
              disabled={!answers[currentQuestion.id] || answers[currentQuestion.id].length === 0}
            >
              ✓ Confirmar Resposta
            </button>
          )}

          {/* Botão Próxima/Finalizar */}
          {(exam.correctionMode !== 'immediate' || showFeedback[currentQuestion.id] || currentQuestion.questionType === 'single_choice') && (
            currentIndex < questions.length - 1 ? (
              <button
                className="btn btn-primary"
                onClick={() => setCurrentIndex(currentIndex + 1)}
              >
                Próxima →
              </button>
            ) : (
              <button
                className="btn btn-success"
                onClick={handleFinish}
              >
                Finalizar Simulado
              </button>
            )
          )}
          <button
            className="btn btn-success"
            onClick={handleFinishEarly}
          >
            Finalizar Agora
          </button>
        </div>

        <div className="ai-panel card">
          <div className="ai-panel-header">
            <div className="ai-panel-title">Assistente IA</div>
            <button
              className="btn btn-ai"
              onClick={handleAiAnswer}
              disabled={aiLoading}
            >
              {aiLoading ? 'Consultando IA...' : 'Responder com IA'}
            </button>
          </div>
          {aiError && <span className="ai-error">{aiError}</span>}
          {aiAnswer?.llm_error && (
            <span className="ai-error">
              LLM indisponível: {aiAnswer.llm_error}
            </span>
          )}
          {aiAnswer && (
            <div className="ai-result">
                <div className="ai-title">Sugestão da IA:</div>
                <div className="ai-suggestion">
                  Ranking: {aiAnswer.suggested_letters?.join(', ') || 'Sem sugestão'}
                </div>
                {aiAnswer.llm?.suggested_letters && (
                  <div className="ai-suggestion ai-suggestion-llm">
                    LLM: {aiAnswer.llm.suggested_letters.join(', ')}
                  </div>
                )}
                {aiAnswer.llm?.explanation && (
                  <div className="ai-explanation">
                    {aiAnswer.llm.explanation}
                  </div>
                )}
                {aiAnswer.options_ranked && aiAnswer.options_ranked.length > 0 && (
                  <div className="ai-ranked">
                    {aiAnswer.options_ranked.slice(0, 3).map(opt => (
                      <div key={opt.letter} className="ai-ranked-item">
                        {opt.letter} - {opt.text} ({(opt.relevance * 100).toFixed(1)}%)
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          <div className="ai-ask">
            <div className="ai-ask-header">
              <div className="ai-title">Perguntar para a IA</div>
              <button
                className="btn btn-ai-secondary"
                onClick={handleAiAsk}
                disabled={askLoading}
              >
                {askLoading ? 'Consultando...' : 'Perguntar'}
              </button>
            </div>
            <textarea
              className="ai-ask-input"
              rows={3}
              placeholder="Ex: O que é CxSegmentTrigger?"
              value={askPrompt}
              onChange={(event) => {
                setAskPrompt(event.target.value);
                if (askError) {
                  setAskError('');
                }
              }}
            />
            {askError && <span className="ai-error">{askError}</span>}
            {askResult?.llm_error && (
              <span className="ai-error">
                LLM indisponível: {askResult.llm_error}
              </span>
            )}
            {askResult?.answer && (
              <div className="ai-ask-result">
                <div className="ai-ask-response">
                  <ReactMarkdown
                    components={{
                      code({node, inline, className, children, ...props}) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={vscDarkPlus}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                      p: ({children}) => <p style={{marginBottom: '12px', lineHeight: '1.6'}}>{children}</p>,
                      ul: ({children}) => <ul style={{marginBottom: '12px', paddingLeft: '20px'}}>{children}</ul>,
                      ol: ({children}) => <ol style={{marginBottom: '12px', paddingLeft: '20px'}}>{children}</ol>,
                      li: ({children}) => <li style={{marginBottom: '6px'}}>{children}</li>,
                      h1: ({children}) => <h1 style={{marginTop: '16px', marginBottom: '12px', fontSize: '1.5em'}}>{children}</h1>,
                      h2: ({children}) => <h2 style={{marginTop: '14px', marginBottom: '10px', fontSize: '1.3em'}}>{children}</h2>,
                      h3: ({children}) => <h3 style={{marginTop: '12px', marginBottom: '8px', fontSize: '1.1em'}}>{children}</h3>,
                    }}
                  >
                    {askResult.answer}
                  </ReactMarkdown>
                </div>
                {askResult.sources && askResult.sources.length > 0 && (
                  <div className="ai-ask-sources">
                    <strong style={{display: 'block', marginBottom: '8px'}}>
                      📚 Fontes ({askResult.sources_breakdown?.pdfs || 0} PDFs + {askResult.sources_breakdown?.codebase || 0} Código):
                    </strong>
                    {askResult.sources.slice(0, 5).map((source, index) => (
                      <div
                        key={`${source.source}-${source.chunk_id}-${index}`}
                        className="ai-ask-source"
                      >
                        {source.source === 'pdfs' ? (
                          <>📚 {source.pdf} p.{source.page} ({(source.relevance * 100).toFixed(1)}%)</>
                        ) : (
                          <>💻 {source.file?.split('/').pop() || 'código'} - {source.module} ({(source.relevance * 100).toFixed(1)}%)</>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExamPage;
